import type { User, Network, OrganisationFunction, Attachment } from '@common/types/objects';
import type { EParentTypes } from '@common/definitions';
import { ShortcutTypes } from '@modules/admin/definitions';
import { ApiResponse } from '@common/services/api/types';
import { CompanyValue } from '@modules/admin/types';

// $Values<...>
type MessageTypes = 'default_message' | 'organisation_message' | 'welcome_message' | 'recognition_message';
type ObjectType = 'feed_message' | 'organisation_message' | 'exchange';
type CreatedIn = { type: EParentTypes, id: string };

export type PollOption = {
  id: string;
  text: string;
  vote_count: number;
};

export type Poll = {
  created_at: string;
  id: string;
  user_id: string;
  message_id?: string;
  options: PollOption[];
  question: string;
  total_vote_count: number;
  vote_result?: string[];
  show_votes: boolean;
  is_multiple_choice: boolean;
  expires_at?: string;
};

export type FeedItem = {
  id: string;
  object_type: ObjectType;
  parent_id: string;
  parent_type: EParentTypes;
  source_id: string;
  user_id: string;
  publish_at?: string;
  processed_at?: string;
};

export type FeedSource = {
  id: string;
  type: ObjectType;
  object_id: string;
  created_in: CreatedIn;
};

export type FeedObject = FeedItem & {
  source: FeedSource;
};



export type Recognition = {
  receiver: User,
  text: string,
  values: CompanyValue[],
  shared: boolean,
};

export type Message = {
  actions: {
    can_delete: boolean;
    can_edit: boolean;
    can_mark_important: boolean;
    can_disable_comments: boolean;
    can_mute: boolean;
    can_see_stats: boolean;
  };
  can_comment: boolean;
  content_language?: string;
  comments_count: number;
  created_at: string;
  created_by: string;
  processed_at?: string;
  created_in: CreatedIn & { values: string; parent: Record<string, unknown> };
  has_liked: boolean;
  has_muted: boolean;
  is_important: boolean;
  id: string;
  likes_count: number;
  message_type: MessageTypes;
  object_id: string;
  seen_count: number;
  text: string;
  type: ObjectType;
  not_seen_count?: number;
  seen?: boolean;
  workflow_id?: string;
  workflow_created_in?: Array<{ name: string }>;
  user?: User;
  user_id: string;
  publish_at?: string;
  poll?: Poll;
  translated_text?: Record<string, string>;
  has_read: boolean;
  recognition?: Recognition;
};

export type Discussion = {
  id: string;
  actions: {
    can_delete_comments: boolean;
  };
};

export type Comment = {
  id: string;
  attachments: Attachment[];
  content_language: string | null;
  text: string;
  message_id: string;
  user?: User;
  likes_count: number;
  has_liked: boolean;
  created_at: string;
  deleted_at: string;
  updated_at?: string;
};

export type ShortcutData = {
  id: string;
  index?: number;
  name?: string;
  enabled?: boolean;
  type?: typeof ShortcutTypes[keyof typeof ShortcutTypes];
  networks: Network[] | null;
  functions: OrganisationFunction[] | null;
  organisation_id?: string;
  web?: {
    url: string;
  };
  target_id?: string;
  oidc_config_id?: string;
  saml_config_id?: string;
};

export type ShortcutIcon = {
  url: string;
  color: string;
};

export type Shortcut = {
  id: string;
  type: typeof ShortcutTypes[keyof typeof ShortcutTypes];
  name: string;
  index: number;
  organisation_id: string;
  data: ShortcutData,
  icon: ShortcutIcon,
};

export type ShortcutResponse = {
  id: string;
  index: number;
  type: typeof ShortcutTypes[keyof typeof ShortcutTypes];
  name: string;
  organisation_id: string;
  icon: string;
  color: string;
  functions: OrganisationFunction[] | null;
  networks: Network[] | null;
  web: {
    url: string;
  };
  target_id?: string;
};

export type FetchShortcutsApiResponse = ApiResponse<Shortcut[]>;
export type FetchShortcutsApiResponseV1 = ApiResponse<ShortcutResponse[]>;

export enum EOrganizationGroupVisibility {
  PUBLIC = 'public',
  PRIVATE = 'private',
  PUBLIC_WITH_APPROVAL = 'public_with_approval',
}

export enum EOrganizationGroupAccessRequestStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
}

export enum EOrganizationGroupClickType {
  LEAVE = 'leave',
  REVOKE = 'revoke',
  JOIN = 'join',
}

export type OrganizationGroup = {
  id: string;
  name: string;
  visibility: EOrganizationGroupVisibility;
  theme:{
    header_image_url: string | null;
    logo_url: string | null;
  };
  is_member: boolean;
  access_request_status: EOrganizationGroupAccessRequestStatus | null;
  member_count: number;
};
